import { forwardRef } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';

import LdsLink from '../LdsLink/index.jsx';
import LdsIcon from '../LdsIcon/index.jsx';

const LdsBreadcrumb = forwardRef(function LdsBreadcrumb({
  ariaLabel = 'Breadcrumb',
  ariaLabelledBy,
  breadcrumbs = [],
  classes,
  ...rest
}, ref) {
  const hasCustomContent = () => {
    // if there are two items without an href, then the last is custom content and should not have the aria-current="page" attribute
    let numNoHrefs = 0;
    for (let i = 0; i < breadcrumbs.length; i += 1) {
      if (!breadcrumbs[i].href) {
        numNoHrefs += 1;
      }
    }
    return numNoHrefs > 1;
  };

  const BreadcrumbItem = ({
    ariaLabel,
    classes,
    clearDefaultClasses,
    href,
    isCurrentPage,
    tabindex,
    target,
    targetAriaLabelContent,
    text,
  }) => {
    if (href) {
      return (
        <>
          <LdsLink
            ariaLabel={ariaLabel}
            classes={cn(
              {
                'lds-breadcrumb': !clearDefaultClasses,
                [`${classes}`]: classes,
              }
            )}
            clearDefaultClasses={clearDefaultClasses}
            disableAutoAriaLabel={!targetAriaLabelContent}
            href={href}
            tabIndex={tabindex}
            target={target}
            targetAriaLabelContent={targetAriaLabelContent}
          >{text}</LdsLink>
        </>
      );
    }
    return (
      <a
        aria-current={isCurrentPage ? 'page' : null}
        aria-label={ariaLabel}
        className={cn(
          {
            'lds-link': !clearDefaultClasses,
            'lds-breadcrumb': !clearDefaultClasses,
            'lds-breadcrumb-content': !isCurrentPage,
            [`${classes}`]: classes,
          }
        )}
        href="#"
        onClick={(e) => e.preventDefault()}
      >{text}</a>
    );
  };

  return (
    <nav
      aria-label={ariaLabel === '' ? null : ariaLabel}
      aria-labelledby={ariaLabelledBy === '' ? null : ariaLabelledBy}
      className={cn(
        {
          [`${classes}`]: classes,
        }
      )}
      ref={ref}
      {...rest}
    >
      <ol
        className="lds-breadcrumbs"
        role="list"
      >
        {breadcrumbs.map((result, idx) => (
          <li
            key={idx}
          >
            <BreadcrumbItem
              ariaLabel={result.ariaLabel}
              classes={result.classes}
              clearDefaultClasses={result.clearDefaultClasses}
              href={result.href}
              isCurrentPage={(((idx + 1) === breadcrumbs.length) && !hasCustomContent()) || (((idx + 2) === breadcrumbs.length) && hasCustomContent())}
              tabindex={result.tabindex}
              target={result.target}
              targetAriaLabelContent={result.targetAriaLabelContent}
              text={result.text}
            />
            {/* Add icon if it's not the last item */}
            {(idx + 1) < breadcrumbs.length && <LdsIcon
                id={`crumb-icon-${idx}`}
                decorative
                inline
                name="CaretRight"
              ></LdsIcon>
            }
          </li>
        ))}
      </ol>
    </nav>
  );
});

LdsBreadcrumb.propTypes = {
  ariaLabel: PropTypes.string,
  ariaLabelledBy: PropTypes.string,
  breadcrumbs: PropTypes.array.isRequired,
  classes: PropTypes.string,
};

export default LdsBreadcrumb;
